// src/encounter/lib/order-payload.ts
// App-side side-map for the structured order data the DS canvas can NOT carry.
//
// The DS AssessmentPlanCanvas types are intentionally lossy: PlanOrder is {id,type,label} ONLY,
// and DxCard exposes {id, code?, display, orders} where `code` is a bare string — neither can
// round-trip the codes/dosage/sub-tests/system that the PatientAssessment wire contract needs.
// So the structured payload lives here, keyed by PlanOrder.id (orders) and DxCard.id (diagnoses),
// alongside the canvas state. The save mapper (a later slice) reads these maps to build the
// AssessmentPlan / Medication / Radiology / Referral bodies.
//
// These types are deliberately CodeDisplayName/Code-ish (the minimal shape the canvas drops);
// the save mapper widens them into the full wire Code/CodeDisplayName/ProcedureCode objects.
//
// VACCINATION (PROD-1227) is the exception: it carries the FULL AssessmentPlanVaccination wire
// entry verbatim, not a minimal coding. A vaccination administration has ~20 lossy fields (lot,
// expiry, site, route, dose, duration, repeat-pattern, isAdministrationRequested, server ids…)
// and round-trips through the canvas precisely so a canvas autosave re-emits it — otherwise
// toPatientAssessment would PUT each Dx with assessmentPlanVaccination:[] and WIPE nested
// vaccinations (doctor requests + nurse administrations alike). Carrying the whole entry keeps
// the round-trip lossless; the save mapper only re-stamps parent/encounter/identifier on it.
import type { AssessmentPlanVaccination, ReferralStatus } from '@/types/assessment-api';

/** Minimal coding the canvas can't hold (its DxCard.code is a bare string, no system/display). */
export interface OrderCoding {
  code: string;
  displayName: string;
  /** Coding system (e.g. loinc, rxnorm, cpt). Optional — not always known at attach time. */
  system?: string;
}

/**
 * The lab vendor on a lab order (PROD-2653): what the save needs for the wire `orderLabs` object,
 * plus the server's own row ids so a re-save updates that row instead of adding another.
 */
export interface LabVendorRef {
  /** LabVendorDto.id → orderLabs.labId. */
  id: string;
  /** → orderLabs.labName. */
  name: string;
  /** → orderLabs.labAddress. */
  address?: string | null;
  /** → orderLabs.labContactNo. */
  phone?: string | null;
  /** The persisted OrderLabs row id (reload only). */
  orderLabsId?: string | null;
  /** The persisted OrderLabs.serviceOrderId (reload only). */
  serviceOrderId?: string | null;
}

/** PROD-2849: one clinic panel test's lab on the wire (Patient `LabOrderMemberLab`). No cost: Patient looks it up. */
export interface LabMemberLab {
  code: string;
  system?: string;
  fulfilmentMode: 'ContractedLab' | 'PatientChoice';
  labVendorId?: string;
  labVendorName?: string;
  labPriceLineId?: string;
}

/** PROD-2849: one answer to a lab order question (Patient `LabOrderAoeAnswer`). */
export interface LabAoeAnswer {
  questionCode?: string;
  questionText: string;
  answerType?: string;
  value: string;
}

/** PROD-2849: the order-search item behind a lab order, kept for the session (see `orderSearch`). */
export interface LabOrderSearchRef {
  itemId: string;
  /** "Single test" | "Panel" | "Clinic panel" (after a reload: derived from the saved order, see from-patient-assessment). */
  type: string;
  /** The patient prep line, e.g. "Fasting 8 h preferred". */
  prep?: string;
  /** The whole order goes as Patient's choice of lab. */
  patientsChoice?: boolean;
  /** A clinic panel's tests that go as Patient's choice of lab (names). */
  patientsChoiceTests?: string[];
}

/** The structured payload behind one PlanOrder, discriminated by OrderType (`kind`). */
export type CodedOrderPayload =
  | {
      kind: 'medication';
  /**
   * Drug-interaction alerts as PERSISTED (Flutter `vidalAlertRequest`). Carried so a re-save preserves
   * what is already on the row: the save mapper hardcoded `[]`, which did not merely fail to record
   * alerts — it ERASED any that Flutter had written, deleting the evidence that a prescriber was warned
   * and prescribed anyway (PROD-1801).
   *
   * Opaque on purpose. We preserve the server's shape rather than re-modelling it; CAPTURING new alerts
   * from the dose dialog's live engine is the separate half, still open.
   */
  vidalAlerts?: unknown[];
      medicationCode: OrderCoding;
      route?: OrderCoding;
      /** Dose unit (e.g. mg/ml/tablet) → wire `unitOfMeasurement`. BE-required (PROD-1339). */
      doseUnit?: string;
      /** Coded frequency type (every_day/as_needed/now/days_interval/specific_days) → wire `frequency`. */
      frequency?: string;
      // ── Occurrence inputs (PROD-1340) — drive the wire occurenceRequest[] per frequency type. ──
      /** Units per intake → occurenceRequest.count. */
      quantity?: number;
      /** Times a day → occurenceRequest.period. */
      timesADay?: number;
      /** Every N days (days_interval) → occurenceRequest.daysInterval. */
      daysInterval?: number;
      /** Weekdays (specific_days) → one occurenceRequest each. */
      specificDays?: string[];
      /** Start date (scheduled frequencies) → wire `onSetDate` (no-Z ISO). */
      startDate?: string;
      duration?: number;
      durationUnit?: string;
      /** Dispense quantity (>0). BE-required (PROD-1339). */
      dispense?: number;
      /** Dispense unit → wire `dispenseUnit`. BE-required (PROD-1339). */
      dispenseUnit?: string;
      refill?: number;
      substitution?: boolean;
      sig?: string;
      note?: string;
      /** Patient weight (frequency `now` only). */
      weight?: number;
      /** Order priority (frequency `now` only): routine/urgent/asap/stat. */
      priority?: string;
      /**
       * When the medication was ORDERED, as the server stamped it (wire `orderDate`, PROD-2854).
       * Read from the server and sent back unchanged, like Flutter (AssessmentPlanMedication keeps it
       * through copyWith and writes it in toJson). Absent on a new order, so the server stamps it once.
       * Never authored by the client — see {@link SERVER_DATE_KEYS}.
       */
      orderDate?: string;
    }
  | {
      kind: 'lab';
      code: OrderCoding;
      /** Lab sub-tests (the value-set test details). Empty when none chosen. */
      subTests: OrderCoding[];
      /**
       * Panel members the clinician deselected (PROD-2632) → wire `omittedTests`. `undefined` means
       * UNKNOWN (the server returned null, or the members never loaded) and the field is not sent;
       * `[]` means known, nothing omitted. Never overlaps `subTests`.
       *
       * INVARIANT: keep `undefined` and `[]` distinct, and never default one to the other. The wire
       * (Patient #988) treats a missing field and `[]` the same today, so the FE is the only place the
       * difference is held. Collapsing them would silently claim "nothing omitted" for orders whose
       * omissions we don't know, if the BE ever starts telling the two apart.
       */
      omittedTests?: OrderCoding[];
      /**
       * The performing lab the order is sent to (PROD-2653) → wire `orderLabs`. `undefined` = not
       * set, and the save sends `orderLabs: null` exactly as before. Labs only; imaging never has it.
       */
      vendor?: LabVendorRef;
      /**
       * PROD-2640: the lab catalogue v2 panel version the order was placed on → wire
       * `panelDefinitionId` / `panelVersion`. Set ONLY when the lab was picked from a v2 published
       * panel; absent otherwise, and the save then sends neither key (the legacy wire, unchanged).
       * subTests / omittedTests stay the snapshot either way, for the readers that predate v2.
       */
      panelDefinitionId?: string;
      panelVersion?: number;
      /**
       * PROD-2640 (Patient #1000, P-B6): who the lab order is billed to → wire `billTo`. The popup
       * always sets it (default Clinic for every vendor, Andrew's ruling); absent only on an order
       * built elsewhere or read back without one, and the save then sends no key.
       */
      billTo?: 'Patient' | 'Clinic';
      /**
       * PROD-2640 (Patient #1001, P-B7, artboard 8): panel members another lab on this plan already
       * orders → wire `alreadyOrderedTests` ({code, system, display}, like omittedTests). Never
       * overlaps `subTests` or `omittedTests`. The catalogue member's code/system VERBATIM; a nested
       * panel is one entry that covers all of its tests. Absent = none / unknown, and not sent.
       */
      alreadyOrderedTests?: OrderCoding[];
      /**
       * PROD-2849 (Patient #1022, PROD-2841): "ContractedLab" (the clinic sends it to `vendor` and bills it)
       * or "PatientChoice" (Patient's choice of lab: not sent, not billed). Set only by the Add lab popup on
       * Master's order-search; absent = a legacy order, and the save sends no key.
       */
      fulfilmentMode?: 'ContractedLab' | 'PatientChoice';
      /** PROD-2849: the chosen lab's price line (Master LabPriceLine.Id). The cost is looked up server-side. */
      labPriceLineId?: string;
      /** PROD-2849: a clinic panel's tests with their own lab or Patient's choice, matched by code + system. */
      memberLabs?: LabMemberLab[];
      /**
       * PROD-2861 (Patient #1028): the Master order-search item the popup placed the order from → wire
       * `labItemId`. Patient stores and returns it, so a reload rebuilds `orderSearch` and the popup reopens
       * the same item; at sign Patient reads the item's patient prep for the printed Post-Visit Summary.
       */
      labItemId?: string;
      /** PROD-2849 (Patient PROD-2640 P-B6): the answers to the lab's order questions. */
      aoeAnswers?: LabAoeAnswer[];
      /**
       * PROD-2849: what the popup ordered from Master's order-search, so an edit reopens the same item and
       * wrap-up can show the patient prep and the Patient's choice tests. Never sent. PROD-2861: rebuilt on
       * a reload from the saved fulfilmentMode / memberLabs / labItemId (without the prep, which the
       * popup reads again from Master when the order is reopened).
       */
      orderSearch?: LabOrderSearchRef;
      /**
       * DISPLAY ONLY (PROD-2640 plan row, design 6.18 / 8.8), never sent: the specimen of the
       * ordered tests (e.g. "Serum · SST") and whether the lab orders it as ONE panel (a CPT or lab
       * panel). Set by the Add lab popup from the catalogue v2 panel; absent after a reload (the
       * A&P read carries neither), where the row falls back to the test count.
       */
      specimen?: string;
      orderedAsOnePanel?: boolean;
      note?: string;
    }
  | {
      kind: 'imaging';
      code: OrderCoding;
      note?: string;
    }
  | {
      kind: 'procedure';
      /** CPT (procedureCode) vs SNOMED (snomedCode) — picks the wire slot in the save mapper. */
      system: 'cpt' | 'snomed';
      code: OrderCoding;
      addToBilling?: boolean;
      note?: string;
      /**
       * The procedure plan's dates as the server returned them (PROD-2854). The server stores the
       * procedure's order date from `endDate` (`OrderDate = EndDate ?? UtcNow`) and returns it there,
       * so dropping it re-stamped every procedure on every save. Flutter's AssessmentPlan round-trips
       * both. Absent on a new order.
       */
      onSetDate?: string;
      endDate?: string;
    }
  | {
      kind: 'referral';
      target?: string;
      additionalDiagnoses?: OrderCoding[];
      note?: string;
      /** PROD-1395: the role referred to, lower case ("dietitian"). Since PROD-3336 sent only for a dietetics speciality. */
      performerRole?: string;
      /** The practitioner picked (guid); null = any practitioner of the speciality. Sent with a speciality (or role) referral. */
      targetPractitionerId?: string | null;
      /** The practitioner name when one is picked, else the speciality (or role) name. */
      targetDisplay?: string;
      /** PROD-3336 (`referrals` flag): the clinic speciality referred to (GUID) and its name. Present = a speciality referral. */
      targetSpecialityId?: string;
      targetSpecialityName?: string;
      /** Server-owned, kept so a re-save keeps the row; NEVER sent. */
      status?: ReferralStatus;
      rootId?: string;
      /** The referral's dates as loaded (PROD-2854) — Flutter's AssessmentReferral round-trips both. */
      onSetDate?: string;
      endDate?: string;
    }
  | {
      // Carries the full nested wire entry (lossless round-trip — see header). The save mapper
      // re-stamps parentId/encounter/subject/performer/identifier; everything else is verbatim.
      kind: 'vaccination';
      entry: AssessmentPlanVaccination;
    };

/** orderId (PlanOrder.id) → structured payload. */
export type OrderPayloadMap = Map<string, CodedOrderPayload>;

/**
 * Prefix marking a CLIENT-SYNTHESIZED fallback id (not server-assigned). The load mapper assigns
 * these to hydrated order rows that arrived WITHOUT a server id, so distinct id-less orders don't
 * all collapse to '' (last-writer-wins in the OrderPayloadMap / a removeOrderPayload('') nuking
 * them all). Because these ids are NOT server-assigned, the save mapper must NOT echo them back as
 * node identifiers — see serverIdOf / collectServerIds, which strip this prefix.
 */
export const SYNTHETIC_ID_PREFIX = 'local::';

/** True when an id was synthesized client-side (never server-assigned). */
export function isSyntheticId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(SYNTHETIC_ID_PREFIX);
}

/** Diagnosis coding the canvas drops (DxCard.code is a bare string, no system/display split). */
export interface DxCoding {
  code?: string;
  display: string;
  system?: string;
}

/** dxId (DxCard.id) → preserved Dx coding (keeps CodeSearchResult.system the canvas discards). */
export type DxCodingMap = Map<string, DxCoding>;

// --- Server-stamped order dates (PROD-2854) ---
//
// The Patient service writes the plan rows again on every save and stamps `now` on any order date the
// request leaves empty (medication `OrderDate = orderDate ?? UtcNow`; procedure `OrderDate = EndDate ??
// UtcNow`). So a date we load and fail to send back is RE-STAMPED with the time of that save. Flutter
// keeps them: each model reads the date, keeps it through copyWith and writes it in toJson. These are
// the dates per payload kind that we hold the same way. Labs/imaging carry none (Flutter's
// AssessmentRadiology round-trips no date); a vaccination keeps its whole wire entry, but an edit
// rebuilds that entry, so its two audit dates are listed too.
export const SERVER_DATE_KEYS = {
  medication: ['orderDate'],
  procedure: ['onSetDate', 'endDate'],
  // status/rootId are server-owned (PROD-1395): an edit keeps them on the payload; they are never sent.
  referral: ['onSetDate', 'endDate', 'status', 'rootId'],
  vaccination: ['createdDate', 'updatedDate'],
  lab: [],
  imaging: [],
} as const satisfies Record<CodedOrderPayload['kind'], readonly string[]>;

/**
 * An EDIT keeps the order's original dates, as in Flutter (copyWith keeps `orderDate`). The edit
 * dialogs build a fresh payload, which has no dates; this copies the previous payload's dates onto it
 * wherever the new one has none. Different kinds (or no previous payload, i.e. a new order) → the new
 * payload as it is. PURE.
 */
export function carryServerDates(
  prev: CodedOrderPayload | undefined,
  next: CodedOrderPayload,
): CodedOrderPayload {
  if (!prev || prev.kind !== next.kind) return next;
  if (next.kind === 'vaccination' && prev.kind === 'vaccination') {
    const entry = { ...next.entry };
    let changed = false;
    for (const k of SERVER_DATE_KEYS.vaccination) {
      if (entry[k] == null && prev.entry[k] != null) {
        entry[k] = prev.entry[k];
        changed = true;
      }
    }
    return changed ? { ...next, entry } : next;
  }
  const from = prev as unknown as Record<string, unknown>;
  const out = { ...next } as unknown as Record<string, unknown>;
  let changed = false;
  for (const k of SERVER_DATE_KEYS[next.kind] as readonly string[]) {
    if (out[k] == null && from[k] != null) {
      out[k] = from[k];
      changed = true;
    }
  }
  return changed ? (out as unknown as CodedOrderPayload) : next;
}

/**
 * The same payload with no server-stamped dates — for an order that is NEW to this visit although it
 * was built from a stored row (a bundle import, an applied order set). It is then sent without a date
 * and the server stamps it once, like any new order. PURE.
 */
export function withoutServerDates(payload: CodedOrderPayload): CodedOrderPayload {
  if (payload.kind === 'vaccination') {
    if (payload.entry.createdDate == null && payload.entry.updatedDate == null) return payload;
    return { ...payload, entry: { ...payload.entry, createdDate: null, updatedDate: null } };
  }
  const keys = SERVER_DATE_KEYS[payload.kind] as readonly string[];
  const src = payload as unknown as Record<string, unknown>;
  if (!keys.some((k) => k in src)) return payload;
  const out = { ...src };
  for (const k of keys) delete out[k];
  return out as unknown as CodedOrderPayload;
}

/**
 * Stamp the order date on an order the physician is ADDING (PROD-2854), once, at creation. The A/P
 * save writes a fresh copy of the plan every time, and the client never learns the stamp the server
 * gives a dateless order, so an order added in this session would be re-stamped on every later save
 * until a reload. Stamping here gives it a date that then round-trips like a loaded one (and an edit
 * keeps it, via {@link carryServerDates}).
 *
 * Only the dates the server would otherwise fill with `now`: medication `orderDate` and the procedure
 * plan's `endDate` (which the server reads as its OrderDate). A date already present is kept. Labs,
 * imaging, referrals and vaccinations are returned as they are: the server stamps none of their dates
 * from the request (a vaccination's createdDate is always the server's own). `now` is the current
 * UTC time as ISO; injectable for tests. PURE apart from the default clock.
 */
export function stampNewOrderDates(
  payload: CodedOrderPayload,
  now: string = new Date().toISOString(),
): CodedOrderPayload {
  if (payload.kind === 'medication' && !payload.orderDate) return { ...payload, orderDate: now };
  if (payload.kind === 'procedure' && !payload.endDate) return { ...payload, endDate: now };
  return payload;
}

// --- OrderPayloadMap helpers (immutable copies) ---

/**
 * Set an order's payload (PROD-2854 — the order-date lifecycle lives here, so every caller gets it):
 *  - FIRST payload for this order id = the physician is ADDING the order (the page's attachOrder, which
 *    every add path goes through, order-set apply included). Its order date is stamped now, once —
 *    see {@link stampNewOrderDates}. A loaded document never comes through here (the seed sets the
 *    map whole), so a server date is never overwritten.
 *  - REPLACING an existing payload (an edit, or the interaction alerts) keeps that order's dates —
 *    see {@link carryServerDates}.
 */
export function setOrderPayload(
  map: OrderPayloadMap,
  orderId: string,
  payload: CodedOrderPayload,
): OrderPayloadMap {
  const prev = map.get(orderId);
  const next = new Map(map);
  next.set(orderId, prev ? carryServerDates(prev, payload) : stampNewOrderDates(payload));
  return next;
}

export function getOrderPayload(
  map: OrderPayloadMap,
  orderId: string,
): CodedOrderPayload | undefined {
  return map.get(orderId);
}

export function removeOrderPayload(map: OrderPayloadMap, orderId: string): OrderPayloadMap {
  const next = new Map(map);
  next.delete(orderId);
  return next;
}

// --- DxCodingMap helpers (immutable copies) ---

export function setDxCoding(map: DxCodingMap, dxId: string, coding: DxCoding): DxCodingMap {
  const next = new Map(map);
  next.set(dxId, coding);
  return next;
}

export function getDxCoding(map: DxCodingMap, dxId: string): DxCoding | undefined {
  return map.get(dxId);
}

export function removeDxCoding(map: DxCodingMap, dxId: string): DxCodingMap {
  const next = new Map(map);
  next.delete(dxId);
  return next;
}
